import { schemaResolver, useForm } from '@mantine/form';
import { DueDateDelay, type WorkflowStepsAttributes } from 'api/models';
import { i18nKeys, useTranslation } from 'locales';
import { useCreateTaskType, useLoadTaskTypes } from 'modules/Tasks';
import { isNonNullish } from 'remeda';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { z } from 'zod/v4';

const ACCOUNT_MANAGER_VALUE = 'auto';

const WorkflowTaskStepFormSchema = z
  .object({
    title: z.string().min(1),
    description: z.string(),
    taskTypeId: z.string().min(1),
    userId: z.string().min(1),
    dueDateDelay: z.string().min(1),
    mandatory: z.string().min(1),
    delay: z
      .union([z.number(), z.literal('')])
      .refine(value => typeof value === 'number' && value >= 1, {
        message: 'required',
      }),
    isAddStartDateChecked: z.boolean(),
    startOffsetDays: z.union([z.number(), z.literal('')]),
    additionalAssigneeIds: z.array(z.string()).default([]),
  })
  .refine(
    values =>
      !values.isAddStartDateChecked ||
      (typeof values.startOffsetDays === 'number' && values.startOffsetDays >= 1),
    { path: ['startOffsetDays'], message: 'required' },
  );

type WorkflowTaskStepFormValues = z.infer<typeof WorkflowTaskStepFormSchema>;

const toInitialValues = (step?: WorkflowStepsAttributes): WorkflowTaskStepFormValues => {
  const initialTaskTypeId = step?.task_attributes?.task_type_id;
  const initialUserId = step?.task_attributes?.user_id;
  const initialDueDateDelay = step?.task_attributes?.due_date_delay;
  const initialMandatory = step?.task_attributes?.mandatory;
  const initialStartOffsetDays = (step?.task_attributes as { start_offset_days?: number })
    ?.start_offset_days;
  const initialAdditionalAssigneeIds = step?.task_attributes?.additional_assignee_ids;

  return {
    title: step?.task_attributes?.title ?? '',
    description: step?.task_attributes?.description ?? '',
    taskTypeId: isNonNullish(initialTaskTypeId) ? String(initialTaskTypeId) : '',
    userId: isNonNullish(initialUserId) ? String(initialUserId) : '',
    dueDateDelay: isNonNullish(initialDueDateDelay) ? String(initialDueDateDelay) : '',
    mandatory: isNonNullish(initialMandatory) ? String(initialMandatory) : '',
    delay: step?.delay ?? '',
    isAddStartDateChecked: isNonNullish(initialStartOffsetDays),
    startOffsetDays: initialStartOffsetDays ?? '',
    additionalAssigneeIds: initialAdditionalAssigneeIds?.map(String) ?? [],
  };
};

const buildTaskStep = (
  step: WorkflowStepsAttributes | undefined,
  values: WorkflowTaskStepFormValues,
): WorkflowStepsAttributes =>
  ({
    ...(step as WorkflowStepsAttributes),
    step_type: 'task_generation',
    delay: Number(values.delay),
    task_attributes: {
      title: values.title,
      description: values.description,
      task_type_id: values.taskTypeId,
      user_id: values.userId,
      due_date_delay: values.dueDateDelay,
      mandatory: values.mandatory,
      additional_assignee_ids: values.additionalAssigneeIds
        .filter(id => id !== values.userId)
        .map(Number),
      ...(values.isAddStartDateChecked && typeof values.startOffsetDays === 'number'
        ? { start_offset_days: values.startOffsetDays }
        : {}),
    },
  }) as unknown as WorkflowStepsAttributes;

type UseWorkflowTaskStepFormOptions = {
  step?: WorkflowStepsAttributes;
  onSubmit: (step: WorkflowStepsAttributes) => void;
};

export const useWorkflowTaskStepForm = ({ step, onSubmit }: UseWorkflowTaskStepFormOptions) => {
  const { t } = useTranslation();
  const { taskTypes } = useLoadTaskTypes();
  const { users = [] } = useLoadLightUsers();
  const { createTaskType } = useCreateTaskType();

  const form = useForm<WorkflowTaskStepFormValues>({
    initialValues: toInitialValues(step),
    validate: schemaResolver(WorkflowTaskStepFormSchema, { sync: true }),
  });

  const dueDateLabel = (days: number) => {
    if (days === 0) return t(i18nKeys.CREATION_DATE, { days });
    if (days === 999) return t(i18nKeys.NEXT_STEP_DATE, { days });
    return t(i18nKeys.N_DAYS_AFTER_CREATION, { days });
  };

  const taskTypeOptions = (taskTypes?.data ?? []).map(type => ({
    value: type.id.toString(),
    label: type.name,
  }));

  const assigneeOptions = [
    { value: ACCOUNT_MANAGER_VALUE, label: t(i18nKeys.ACCOUNT_MANAGER) },
    ...users.map(assignee => ({
      value: assignee.id.toString(),
      label: assignee.name.full,
    })),
  ];

  const dueDateOptions = Object.values(DueDateDelay).map(days => ({
    value: days.toString(),
    label: dueDateLabel(days),
  }));

  const mandatoryOptions = [
    { value: 'true', label: t(i18nKeys.YES) },
    { value: 'false', label: t(i18nKeys.NO) },
  ];

  const submit = form.onSubmit(values => onSubmit(buildTaskStep(step, values)));

  return {
    form,
    taskTypeOptions,
    assigneeOptions,
    dueDateOptions,
    mandatoryOptions,
    users,
    submit,
    createTaskType,
  };
};
